import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ModulesProvider } from "@/context/ModulesContext";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import ModuleGate from "@/components/ModuleGate";
import SuspendedScreen from "@/components/SuspendedScreen";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminConsole from "@/pages/admin/AdminConsole";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminAssistant from "@/pages/admin/AdminAssistant";
import AdminPricing from "@/pages/admin/AdminPricing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import Unscheduled from "@/pages/Unscheduled";
import Register from "@/pages/Register";
import Prepayments from "@/pages/Prepayments";
import Accruals from "@/pages/Accruals";
import Reconciliation from "@/pages/Reconciliation";
import Audit from "@/pages/Audit";
import Reports from "@/pages/Reports";
import Forecast from "@/pages/Forecast";
import Movement from "@/pages/Movement";
import Insights from "@/pages/Insights";
import Close from "@/pages/Close";
import Approvals from "@/pages/Approvals";
import DeferredIncome from "@/pages/DeferredIncome";
import ActionInbox from "@/pages/ActionInbox";
import DeferredReports from "@/pages/DeferredReports";
import FixedAssets from "@/pages/FixedAssets";
import Portfolio from "@/pages/Portfolio";
import CommandCentre from "@/pages/CommandCentre";
import PracticeSettings from "@/pages/PracticeSettings";
import Subscription from "@/pages/Subscription";
import MyAccount from "@/pages/MyAccount";
import HelpCenter from "@/pages/HelpCenter";
import Learning from "@/pages/Learning";
import RecurringAccrualsPage from "@/pages/RecurringAccrualsPage";
import TreatmentRulesPage from "@/pages/TreatmentRulesPage";
import AccrualAccuracyPage from "@/pages/AccrualAccuracyPage";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import SetPassword from "@/pages/SetPassword";
import LockScreen from "@/components/LockScreen";
import CommandPalette from "@/components/CommandPalette";
import MarketingHome from "@/pages/marketing/MarketingHome";
import Features from "@/pages/marketing/Features";
import Pricing from "@/pages/marketing/Pricing";
import Compare from "@/pages/marketing/Compare";
import FAQ from "@/pages/marketing/FAQ";
import GuidesIndex from "@/pages/marketing/GuidesIndex";
import GuideArticle from "@/pages/marketing/GuideArticle";
import AdminGuides from "@/pages/admin/AdminGuides";
import LearningStudio from "@/pages/admin/LearningStudio";
import AdminSocial from "@/pages/admin/AdminSocial";
import AdminLaunch from "@/pages/admin/AdminLaunch";
import Onboarding from "@/pages/Onboarding";
import PaymentReturn from "@/pages/PaymentReturn";
import StartTrial from "@/pages/marketing/StartTrial";
import Security from "@/pages/marketing/Security";

const SUSPEND_OK_PATHS = ["/subscription", "/account", "/help"];

function Protected({ children }) {
  const { user, account, locked } = useAuth();
  const loc = useLocation();
  if (user === null)
    return (
      <div className="h-screen flex items-center justify-center bg-appbg">
        <span className="font-mono text-navy/70 text-sm tracking-widest">LOADING_DATA…</span>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  // Platform owner lives in the back-office, not the practice app.
  if (user.role === "superadmin") return <Navigate to="/admin" replace />;
  // Suspended practice: block the app except billing/account/help so they can pay.
  if (account?.status === "suspended" && !SUSPEND_OK_PATHS.includes(loc.pathname)) {
    return <SuspendedScreen />;
  }
  return (
    <>
      <Layout>{children}</Layout>
      <CommandPalette />
      {locked && <LockScreen />}
    </>
  );
}

function SuperadminRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="h-screen flex items-center justify-center bg-appbg">
        <span className="font-mono text-navy/70 text-sm tracking-widest">LOADING_DATA…</span>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "superadmin") return <Navigate to="/app" replace />;
  return children;
}

function IndexRoute() {
  const [dest, setDest] = useState(null);
  useEffect(() => {
    api.get("/xero/status")
      .then(({ data }) => setDest((data.organisations || []).length > 1 ? "/command-centre" : "/dashboard"))
      .catch(() => setDest("/dashboard"));
  }, []);
  if (!dest)
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <span className="font-mono text-navy/70 text-sm tracking-widest">LOADING_DATA…</span>
      </div>
    );
  return <Navigate to={dest} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ModulesProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<MarketingHome />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/security" element={<Security />} />
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<GuideArticle />} />
            <Route path="/start" element={<StartTrial />} />
            <Route path="/payment/return" element={<PaymentReturn />} />
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/app" element={<Protected><IndexRoute /></Protected>} />
            <Route path="/admin" element={<SuperadminRoute><AdminDashboard /></SuperadminRoute>} />
            <Route path="/admin/accounts" element={<SuperadminRoute><AdminConsole /></SuperadminRoute>} />
            <Route path="/admin/leads" element={<SuperadminRoute><AdminLeads /></SuperadminRoute>} />
            <Route path="/admin/assistant" element={<SuperadminRoute><AdminAssistant /></SuperadminRoute>} />
            <Route path="/admin/pricing" element={<SuperadminRoute><AdminPricing /></SuperadminRoute>} />
            <Route path="/admin/guides" element={<SuperadminRoute><AdminGuides /></SuperadminRoute>} />
            <Route path="/admin/learning" element={<SuperadminRoute><LearningStudio /></SuperadminRoute>} />
            <Route path="/admin/social" element={<SuperadminRoute><AdminSocial /></SuperadminRoute>} />
            <Route path="/admin/launch" element={<SuperadminRoute><AdminLaunch /></SuperadminRoute>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/inbox" element={<Protected><ActionInbox /></Protected>} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/connections" element={<Navigate to="/settings?tab=connect" replace />} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/practice" element={<Protected><PracticeSettings /></Protected>} />
            <Route path="/subscription" element={<Protected><Subscription /></Protected>} />
            <Route path="/account" element={<Protected><MyAccount /></Protected>} />
            <Route path="/unscheduled" element={<Protected><Unscheduled /></Protected>} />
            <Route path="/register" element={<Protected><Register /></Protected>} />
            <Route path="/prepayments" element={<Protected><ModuleGate moduleKey="prepayments"><Prepayments /></ModuleGate></Protected>} />
            <Route path="/accruals" element={<Protected><ModuleGate moduleKey="accruals"><Accruals /></ModuleGate></Protected>} />
            <Route path="/forecast" element={<Protected><Forecast /></Protected>} />
            <Route path="/movement" element={<Protected><Movement /></Protected>} />
            <Route path="/reconciliation" element={<Protected><Reconciliation /></Protected>} />
            <Route path="/insights" element={<Protected><Insights /></Protected>} />
            <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/deferred-revenue" element={<Navigate to="/deferred-income" replace />} />
            <Route path="/deferred-income" element={<Protected><ModuleGate moduleKey="deferred_income"><DeferredIncome /></ModuleGate></Protected>} />
            <Route path="/deferred-reports" element={<Protected><DeferredReports /></Protected>} />
            <Route path="/fixed-assets" element={<Protected><ModuleGate moduleKey="fixed_assets"><FixedAssets /></ModuleGate></Protected>} />
            <Route path="/recurring-accruals" element={<Protected><RecurringAccrualsPage /></Protected>} />
            <Route path="/treatment-rules" element={<Protected><TreatmentRulesPage /></Protected>} />
            <Route path="/accrual-accuracy" element={<Protected><AccrualAccuracyPage /></Protected>} />
            <Route path="/prepayment-variance" element={<Navigate to="/reconciliation?tab=prepayment" replace />} />
            <Route path="/deferred-income-variance" element={<Navigate to="/reconciliation?tab=deferred-income" replace />} />
            <Route path="/portfolio" element={<Protected><Portfolio /></Protected>} />
            <Route path="/command-centre" element={<Protected><CommandCentre /></Protected>} />
            <Route path="/close" element={<Protected><Close /></Protected>} />
            <Route path="/audit" element={<Protected><Audit /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/help" element={<Protected><HelpCenter /></Protected>} />
            <Route path="/learning" element={<Protected><Learning /></Protected>} />
            <Route path="/users" element={<Navigate to="/practice?tab=team" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ModulesProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
